import ProductHero from "@/components/ProductHero";
import ProductDetail from "@/components/ProductDetail";
import ProductCTA from "@/components/ProductCTA";
import SEO from "@/components/SEO";
import SystemCombinations from "@/components/SystemCombinations";
import RelatedSystems from "@/components/RelatedSystems";
import GeoDefinition, { buildDefinedTerm, buildFaqJsonLd } from "@/components/GeoDefinition";
import ProductFaq from "@/components/ProductFaq";
import {
  IntroBlock,
  ProblemBlock,
  ExperienceBlock,
  AudienceBlock,
  BenefitsBlock,
} from "@/components/SectionBlocks";
import { SYSTEMS } from "@/lib/constants";

const s = SYSTEMS.find((x) => x.slug === "pine-vr");

const PINEVR_DEFINITION =
  "PINE VR ist das kompakte, professionelle VR-Standortprodukt von PINE Systems für kleinere Freizeitflächen, Eventlocations und bestehende Indoor-Betriebe. Betreute VR-Erlebnisformate wie virtuelle Escape Rooms und kooperative Missionen werden auf kompakter Fläche professionell betreibbar gemacht.";

const PINEVR_PRODUCT_FAQ = [
  {
    q: "Was ist der Unterschied zwischen PINE VR und PVRA?",
    a: "PINE VR ist die kompakte Variante: kleinere Gruppen, geringere Fläche, einfachere Integration in bestehende Betriebe. PVRA ist das große Arena-System mit mehreren parallelen Erlebnisformaten und bis zu 16 Spielern auf zwei Playfields.",
  },
  {
    q: "Kann PINE VR für virtuelle Escape Rooms genutzt werden?",
    a: "Ja. PINE VR ist besonders stark in kooperativen Formaten wie virtuellen Escape Rooms, Rätsel- und Story-Missionen, Familienabenteuern und kleinen Team-Erlebnissen.",
  },
  {
    q: "Für welche Flächen ist PINE VR interessant?",
    a: "Orientierungswert ab ca. 36 m² Spielfläche bzw. ab ca. 70 m² Gesamtfläche bei passenden Standortparametern. Geeignet für kleinere Freizeitstandorte, Eventlocations und bestehende Indoor-Freizeitbetriebe.",
  },
  {
    q: "Ist PINE VR ein Selbstbedienungsprodukt?",
    a: "Nein. PINE VR ist ein betreutes VR-Erlebnisformat. Einweisung, Ablauf, Sicherheit, Qualität und Wiederholbarkeit sind Teil der Systemlogik – nicht reines Hardware-Setup.",
  },
];

const PINEVR_QA_FOR_LD = [
  ...PINEVR_PRODUCT_FAQ,
];

export default function PineVr() {
  return (
    <>
      <SEO
        title="PINE VR – Kompakte VR-Erlebnisse und virtuelle Escape Rooms"
        description="PINE VR ist das kompakte professionelle VR-Standortprodukt für kleinere Flächen, Eventlocations und bestehende Freizeitbetriebe."
        ogType="product"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "PINE VR — Compact Virtual Reality",
            brand: { "@type": "Brand", name: "PINE Systems" },
            description: PINEVR_DEFINITION,
            category: "Location-Based Entertainment System",
          },
          buildDefinedTerm({
            name: "PINE VR",
            alternateName: "Compact Virtual Reality",
            description: PINEVR_DEFINITION,
            url: "https://www.pinexs.de/pine-vr",
          }),
          buildFaqJsonLd(PINEVR_QA_FOR_LD),
        ]}
      />
      <ProductHero
        code="03 / SYSTEM"
        name="PINE VR"
        headline="Das kompakte VR-Standortprodukt mit System."
        sub="PINE VR ist das kompakte professionelle VR-Standortprodukt für Betreiber, die ein betreutes Erlebnisformat integrieren möchten, ohne eine große Arena-Struktur aufzubauen."
        image={s.image}
      />

      <IntroBlock
        overline="Positionierung"
        title="Professionelle VR-Erlebnisse auf kompakter Fläche."
        text={`PINE VR ist für Betreiber gedacht, die ein hochwertiges, betreutes VR-Erlebnisformat integrieren möchten, ohne eine große Arena-Struktur aufzubauen.

Das System eignet sich für kleinere Freizeitstandorte, Eventlocations, bestehende Indoorflächen und Betreiber, die ihren Gästen virtuelle Escape Rooms, kooperative Missionen oder kompakte Gruppenformate anbieten möchten.`}
      />

      <ProblemBlock
        overline="Problem"
        title="Das Problem, das PINE VR löst."
        text="Viele Standorte haben Räume, aber keine klare Idee, wie daraus ein modernes Erlebnisangebot entstehen kann. Gleichzeitig ist eine große Arena nicht für jeden Betreiber sinnvoll oder realistisch. PINE VR schließt diese Lücke: kompakter als PVRA, aber weiterhin professionell, betreut und systematisch aufgebaut."
        cards={[
          {
            title: "Kleine Flächen werden oft unterschätzt",
            text: "Auch kompakte Räume können wertvoll werden, wenn sie klar inszeniert und sinnvoll betrieben werden.",
          },
          {
            title: "Gäste suchen gemeinsame Erlebnisse",
            text: "Gerade kleinere Gruppen wollen Formate, die sie zusammen lösen, erleben und weitererzählen können.",
          },
          {
            title: "Betreiber brauchen einfache Integration",
            text: "Ein neues Angebot muss in den bestehenden Betrieb passen und verständlich zu führen sein.",
          },
        ]}
      />

      <ExperienceBlock
        overline="Erlebnisformate"
        title="Welche Erlebnisse PINE VR möglich macht."
        text="PINE VR eignet sich besonders für kompakte, betreute VR-Gruppenerlebnisse."
        items={[
          "Virtuelle Escape Rooms",
          "Kooperative Abenteuer",
          "Familienmissionen",
          "Rätsel- und Storyformate",
          "Kleine Team-Erlebnisse",
          "Geburtstags- und Gruppenangebote",
          "Event-Ergänzungen",
          "Indoor-Erlebnisangebote für bestehende Freizeitbetriebe",
        ]}
        highlight="Nutzen Sie Flächen ab ca. 36 m² Spielfläche bzw. ab ca. 70 m² Gesamtfläche bei passenden Standortparametern, um daraus ein betreutes VR-Erlebnisangebot zu entwickeln."
      />

      <AudienceBlock
        overline="Zielgruppen"
        title="Für welche Betreiber PINE VR besonders passt."
        cards={[
          {
            title: "Eventlocations",
            text: "Für Locations, die Gruppen ein modernes Zusatzangebot machen möchten.",
          },
          {
            title: "Kleinere Freizeitstandorte",
            text: "Für Betreiber, die mit begrenzter Fläche ein professionelles Erlebnisformat integrieren wollen.",
          },
          {
            title: "Bestehende Indooranbieter",
            text: "Für Betriebe, die ihr Angebot um virtuelle Escape Rooms und kooperative Missionen erweitern möchten.",
          },
          {
            title: "Gründer mit kompakter Fläche",
            text: "Für Gründer, die kleiner starten und trotzdem professionell auftreten möchten.",
          },
        ]}
      />

      <BenefitsBlock
        overline="Nutzen"
        title="Der Nutzen für Ihren Standort."
        items={[
          "Kompakter Einstieg in betreutes XR-Entertainment",
          "Klare Erlebnisformate statt Technikverkauf",
          "Geeignet für kleine Gruppen",
          "Buchbare Missionen und Escape-orientierte Formate",
          "Geringere Parallelkomplexität als große Arena-Systeme",
          "Nutzbar für private Gruppen, Familien, Events und kleinere Firmenformate",
        ]}
      />

      <ProductDetail
        forWhom={[
          "Kleinere Freizeitstandorte",
          "Eventlocations",
          "Bestehende Indoor-Freizeitbetriebe",
          "Kleinere Städte und Orte",
          "Ergänzende Erlebnisflächen",
          "Betreiber mit begrenzterer Fläche",
        ]}
        strengths={[
          "Kompakte Systemlogik",
          "Betreutes VR-Erlebnisformat",
          "Passend für kleinere Gruppen",
          "Kooperative Missionen",
          "Familien- und Escape-orientierte Formate",
        ]}
        orientation={[
          "Bis zu 6 Spieler auf einem Playfield",
          "Ab ca. 70 m² Gesamtfläche bei passenden Standortparametern",
          "Betreutes VR-Erlebnisformat",
        ]}
        ctaLabel="PINE VR Standort prüfen lassen"
      />

      <ProductCTA primaryLabel="PINE VR Standort prüfen lassen" />

      <RelatedSystems currentSlug="pine-vr" />
      <SystemCombinations />

      <ProductFaq
        productSlug="pine-vr"
        title="Häufige Fragen zu PINE VR."
        qa={PINEVR_PRODUCT_FAQ}
      />

      <GeoDefinition
        term="PINE VR"
        overline="GEO · Definition"
        definition={PINEVR_DEFINITION}
        qa={PINEVR_QA_FOR_LD.slice(0, 1)}
        variant="muted"
      />
    </>
  );
}
